/**
 * Better-Auth React client - used by client components for auth actions.
 *
 * Usage in a client component:
 *
 *   'use client'
 *   import { authClient } from '@/lib/auth/client'
 *
 *   await authClient.signIn.email({ email, password })
 *   await authClient.signIn.social({ provider: 'google', callbackURL: '/feed' })
 *   await authClient.signOut()
 *   const { data: session } = authClient.useSession()
 *
 * For server-side session reads, use `requireAuth()` from `./require`
 * (see Phase 2d) - it's faster and avoids a roundtrip.
 */

import { createAuthClient } from 'better-auth/react'
import { twoFactorClient, magicLinkClient } from 'better-auth/client/plugins'

const APP_URL =
  typeof window !== 'undefined'
    ? window.location.origin
    : process.env.NEXT_PUBLIC_APP_URL ?? ''

export const authClient = createAuthClient({
  baseURL: APP_URL,
  plugins: [
    twoFactorClient({
      // Redirect to our challenge page when sign-in surfaces a 2FA gate.
      // The server action at /(auth)/actions.ts also routes here when it
      // detects twoFactorRedirect from auth.api.signInEmail.
      twoFactorPage: '/login/two-factor',
    }),
    magicLinkClient(),
  ],
})

// Re-export the most-used hooks/methods for ergonomic imports.
export const { signIn, signUp, signOut, useSession, getSession } = authClient
